import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Medal, MapPin, Award, X, BadgeCheck, Quote } from "lucide-react";
import "./MedhaTeachers.scss";

import pRamesh   from "../assets/testimonials/medha-ramesh-badoni.jpeg";
import pNaveen   from "../assets/testimonials/medha-naveen-gupta.jpeg";
import pAnju     from "../assets/testimonials/medha-anju-dahiya.jpeg";
import pRajni    from "../assets/testimonials/medha-rajni-sharma.jpeg";
import pOm       from "../assets/testimonials/medha-om-prakash.jpeg";
import pImran    from "../assets/testimonials/medha-imran-khan.jpeg";
import pAlpa     from "../assets/testimonials/medha-alpa-nigam.jpeg";
import pManoj    from "../assets/testimonials/medha-manoj-kumar.jpeg";
import pVirender from "../assets/testimonials/medha-virender-kumar.jpeg";
import pAnita    from "../assets/testimonials/medha-anita-nashier.jpeg";

// `signature` = hero credential shown on the chip. `awards` = full list for the modal.
// `quote` = verbatim Hindi testimonial from the PATF webinar (patfindia.org, June 2026).
const TEACHERS = [
  {
    name: "Mohammad Imran Khan", photo: pImran, star: true,
    role: "Senior Teacher, Govt. Sr. Sec. Sanskrit School", place: "Alwar, Rajasthan",
    signature: "Global Teacher Prize Finalist ’25",
    awards: [
      "Global Teacher Prize Finalist 2025",
      "Dainik Bhaskar National Teacher Award 2026",
      "Jamnalal Bajaj Award 2019",
      "National Award to Teachers 2017",
      "National ICT Teacher Award 2016",
    ],
  },
  {
    name: "Om Prakash Patidar", photo: pOm,
    role: "Senior Lecturer (Biology), Govt. Excellence School", place: "Shajapur, M.P.",
    signature: "National Teachers’ Award ’22",
    quote: "यह शिक्षक के समय की बचत करते हुए शिक्षण को अधिक प्रभावी और छात्र-केंद्रित बना सकता है।",
    awards: [
      "National Teachers' Award 2022",
      "State Teachers Award 2019",
      "National ICT Award 2017",
    ],
  },
  {
    name: "Naveen Gupta", photo: pNaveen,
    role: "Senior Educator", place: "Delhi",
    signature: "National ICT Award ’19",
    quote: "शिक्षकों द्वारा दिए गए सुझावों को जिस प्रकार प्लेटफॉर्म में शामिल किया गया है, वह इसकी सबसे बड़ी विशेषता है।",
    awards: [
      "National ICT Award 2019",
      "Delhi State Best Teacher 2024",
    ],
  },
  {
    name: "Alpa Nigam", photo: pAlpa,
    role: "Head Teacher, Primary School Tilauli", place: "Gorakhpur, U.P.",
    signature: "National ICT Award ’16",
    quote: "मेधा AI को केवल तकनीकी दृष्टिकोण से नहीं बल्कि शिक्षाशास्त्रीय दृष्टिकोण से भी विकसित किया जा रहा है।",
    awards: [
      "National ICT Award 2016",
      "Fulbright DAI 2022–23",
      "State Award 2017",
    ],
  },
  {
    name: "Ramesh Badoni", photo: pRamesh,
    role: "SCERT Uttarakhand", place: "Uttarakhand",
    signature: "NAT–NICT Awardee",
    quote: "मेधा AI का सबसे बड़ा गुण यह है कि इसे शिक्षकों की वास्तविक आवश्यकताओं और कक्षा-कक्ष के अनुभवों के आधार पर विकसित किया गया है।",
    awards: ["NAT–NICT Awardee"],
  },
  {
    name: "Manoj Kumar Singh", photo: pManoj,
    role: "TGT, PM SHRI UHS Khukradih", place: "Jamshedpur, Jharkhand",
    signature: "National Award to Teachers ’21",
    quote: "AI का उपयोग तभी सार्थक है जब वह शिक्षक और विद्यार्थियों दोनों के लिए उपयोगी हो।",
    awards: [
      "National Award to Teachers 2021",
      "State Teachers Award 2018",
      "National Academy of Sciences India 2017",
    ],
  },
  {
    name: "Virender Kumar", photo: pVirender,
    role: "Lecturer, Govt. Sr. Sec. School Domehar (K)", place: "Solan, H.P.",
    signature: "National Award to Teachers ’22",
    awards: [
      "National Award to Teachers 2022",
      "Governor's Award 2023",
      "State Teachers Award 2015",
    ],
  },
  {
    name: "Anita Nashier", photo: pAnita,
    role: "Senior Educator", place: "India",
    signature: "National Award to Teachers ’13",
    quote: "शिक्षकों के अनुभवों को शामिल करना इसकी सबसे बड़ी विशेषता है।",
    awards: [
      "National Award to Teachers 2013",
      "State Teachers Award 2010",
      "Nigam Award 2004",
    ],
  },
  {
    name: "Anju Dahiya", photo: pAnju,
    role: "Senior Educator", place: "Haryana",
    signature: "National Award to Teachers ’22",
    awards: ["National Award to Teachers 2022"],
  },
  {
    name: "Rajni Sharma", photo: pRajni,
    role: "Principal, MCD", place: "Delhi",
    signature: "National Award to Teachers ’22",
    quote: "मेधा AI जैसे प्लेटफॉर्म शिक्षकों को नई तकनीकों से जोड़ने के साथ-साथ विद्यार्थियों के लिए सीखने को अधिक रोचक बना सकते हैं।",
    awards: ["National Award to Teachers 2022"],
  },
];

const QUOTED = TEACHERS.filter((t) => t.quote);

function QuoteCard({ t, onClick }) {
  return (
    <button type="button" className="woh__qcard" onClick={() => onClick(t)}>
      <Quote size={18} strokeWidth={2.2} className="woh__qcard-mark" />
      <p lang="hi" className="woh__qcard-text">{t.quote}</p>
      <span className="woh__qcard-foot">
        <img src={t.photo} alt={t.name} loading="lazy" />
        <span className="woh__qcard-who">
          <b>{t.name}</b>
          <i><Medal size={12} strokeWidth={2} /> {t.signature}</i>
        </span>
      </span>
    </button>
  );
}

function Marquee({ children, dir, speed }) {
  return (
    <div className="woh__marquee">
      <div className={`woh__track woh__track--${dir} woh__track--${speed}`}>
        <div className="woh__seg">{children}</div>
        <div className="woh__seg" aria-hidden="true">{children}</div>
      </div>
    </div>
  );
}

function TeacherModal({ t, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);

    // Lock the page behind the modal; the modal body scrolls on its own.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <motion.div
      className="woh__overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="woh__modal"
        initial={{ opacity: 0, scale: 0.94, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.24, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="woh__modal-close" onClick={onClose} aria-label="Close">
          <X size={18} strokeWidth={2.2} />
        </button>

        <div className="woh__modal-head">
          <div className="woh__modal-avatar-wrap">
            <img src={t.photo} alt={t.name} className="woh__modal-avatar" />
            <span className="woh__modal-verify"><BadgeCheck size={15} strokeWidth={2.5} /></span>
          </div>
          <h3 className="woh__modal-name">{t.name}</h3>
          <p className="woh__modal-role">{t.role}</p>
          <span className="woh__modal-loc"><MapPin size={12} strokeWidth={2} /> {t.place}</span>
        </div>

        {t.quote && (
          <blockquote className="woh__modal-quote">
            <Quote size={16} strokeWidth={2.2} />
            <p lang="hi">{t.quote}</p>
            <cite>PATF Webinar · June 2026</cite>
          </blockquote>
        )}

        <div className="woh__modal-awards">
          <span className="woh__modal-awards-title">
            <Award size={13} strokeWidth={2.2} /> Honours &amp; Awards
            <em>{t.awards.length}</em>
          </span>
          <ul className="woh__modal-list">
            {t.awards.map((a, i) => (
              <li key={i} className="woh__modal-item">
                <Medal size={14} strokeWidth={2} />
                {a}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function MedhaTeachers() {
  const [selected, setSelected] = useState(null);

  return (
    <section className="woh-wrap">
      <div className="woh-inner">
        <motion.div
          className="woh__head"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          <div className="woh__kicker">Trusted by the Best</div>
          <h2 className="woh__h2">
            India's award-winning teachers{" "}<br />
            <span>trust Medha AI</span>
          </h2>
        </motion.div>
      </div>

      <div className="woh__rows">
        <Marquee dir="left" speed="slow">
          {QUOTED.map((t) => (
            <QuoteCard key={t.name} t={t} onClick={setSelected} />
          ))}
        </Marquee>
      </div>

      <p className="woh__hint">Tap a teacher to see their words &amp; honours</p>

      <AnimatePresence>
        {selected && <TeacherModal t={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
